import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useStripe } from '@stripe/react-stripe-js';
import { v2api } from '../../api/axios';
import Copyright from '../molecules/Copyright';
import { ErrorHandler, ProcessedError } from '../../utils/errorHandler';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
}));

const AmountDisplay = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const RetryButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  fontWeight: 'bold',
}));

interface PaymentInfo {
  payment_intent_id: string;
  amount: number;
  currency: string;
  status: string;
  failure_reason: string;
  created: number;
  description: string;
}

interface ProcessingStatus {
  isLoading: boolean;
  isProcessing: boolean;
  isCompleted: boolean;
  error: string | null;
  errorType?: string;
  shouldRetry?: boolean;
  retryAfter?: number;
}

const RetryPayment: React.FC = () => {
  const { paymentIntentId } = useParams<{ paymentIntentId: string }>();
  const [searchParams] = useSearchParams();
  // const navigate = useNavigate(); // 現在未使用
  const stripe = useStripe();

  const token = searchParams.get('token');

  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [status, setStatus] = useState<ProcessingStatus>({
    isLoading: true,
    isProcessing: false,
    isCompleted: false,
    error: null,
  });

  // 決済情報を取得
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        if (!paymentIntentId || !token) {
          setStatus(prev => ({
            ...prev,
            isLoading: false,
            error: 'リンクが無効です。再決済用URLを確認してください。',
          }));
          return;
        }

        const response = await v2api.get(`api/payments/${paymentIntentId}`, {
          params: { token },
        });

        setPaymentInfo(response.data);
        setStatus(prev => ({
          ...prev,
          isLoading: false,
        }));
      } catch (error: any) {
        const processedError = ErrorHandler.processApiError(error);
        ErrorHandler.logError(processedError, 'fetchPaymentInfo', { paymentIntentId, token });

        setStatus(prev => ({
          ...prev,
          isLoading: false,
          error: processedError.userMessage,
          errorType: processedError.errorType,
          shouldRetry: processedError.shouldRetry,
          retryAfter: processedError.retryAfter,
        }));
      }
    };

    fetchPaymentInfo();
  }, [paymentIntentId, token]);

  // 再決済処理
  const handleRetryPayment = async () => {
    try {
      if (!stripe || !paymentIntentId || !token) {
        throw new Error('決済処理の準備ができていません。');
      }

      setStatus(prev => ({
        ...prev,
        isProcessing: true,
        error: null,
      }));

      // 新しいPaymentIntentを作成
      const retryResponse = await v2api.post(`api/payments/${paymentIntentId}/retry`, {
        token,
      });

      const { client_secret, payment_intent_id: newPaymentIntentId } = retryResponse.data;

      // Stripeで3D認証を含む決済を確認
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(client_secret);

      if (stripeError) {
        const processedError = ErrorHandler.processStripeClientError(stripeError);
        ErrorHandler.logError(processedError, 'stripeConfirmCardPayment', { paymentIntentId, newPaymentIntentId });
        throw new Error(processedError.userMessage);
      }

      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
        // 決済成功をサーバーに通知
        await v2api.post(`api/payments/${paymentIntentId}/confirm`, {
          token,
          new_payment_intent_id: newPaymentIntentId,
        });

        setStatus(prev => ({
          ...prev,
          isProcessing: false,
          isCompleted: true,
        }));
      } else {
        throw new Error('決済が完了しませんでした。');
      }
    } catch (error: any) {
      let processedError: ProcessedError;
      
      if (error.response) {
        // API エラー
        processedError = ErrorHandler.processApiError(error);
        ErrorHandler.logError(processedError, 'retryPaymentAPI', { paymentIntentId, token });
      } else {
        // クライアントサイドエラー（Stripe等）
        processedError = {
          userMessage: error.message || '再決済処理に失敗しました。',
          technicalMessage: error.message || 'Client-side error',
          errorType: 'client_error',
          shouldRetry: false
        };
        ErrorHandler.logError(processedError, 'retryPaymentClient', { paymentIntentId, token });
      }

      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        error: processedError.userMessage,
        errorType: processedError.errorType,
        shouldRetry: processedError.shouldRetry,
        retryAfter: processedError.retryAfter,
      }));
    }
  };

  // 金額のフォーマット
  const formatAmount = (amount: number, currency: string) => {
    if (currency.toLowerCase() === 'jpy') {
      return `¥${amount.toLocaleString()}`;
    }
    return `${amount} ${currency.toUpperCase()}`;
  };

  // 日時のフォーマット
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (status.isLoading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            決済情報を読み込んでいます...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (status.error && !paymentInfo) {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <Box textAlign="center">
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              エラーが発生しました
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {status.error}
            </Typography>
            
            {status.retryAfter && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {new Date(status.retryAfter * 1000).toLocaleString()}以降に再試行してください。
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              {status.shouldRetry && (
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  startIcon={<RefreshIcon />}
                  disabled={!!status.retryAfter && status.retryAfter * 1000 > Date.now()}
                >
                  再試行
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => window.location.href = 'https://transrec.net'}
              >
                トップページに戻る
              </Button>
            </Box>
          </Box>
        </StyledPaper>
        <Copyright />
      </Container>
    );
  }

  if (status.isCompleted) {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <Box textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              再決済が完了しました
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              決済が正常に処理されました。ご利用いただきありがとうございます。
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.href = 'https://transrec.net'}
              sx={{ mt: 2 }}
            >
              トップページに戻る
            </Button>
          </Box>
        </StyledPaper>
        <Copyright />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <CreditCardIcon sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                再決済のお手続き
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              決済に失敗したお支払いの再処理を行います。
            </Typography>
          </Grid>

          {paymentInfo && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    決済情報
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      決済金額
                    </Typography>
                    <AmountDisplay>
                      {formatAmount(paymentInfo.amount, paymentInfo.currency)}
                    </AmountDisplay>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      決済日時
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(paymentInfo.created)}
                    </Typography>
                  </Box>

                  {paymentInfo.description && (
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        説明
                      </Typography>
                      <Typography variant="body1">
                        {paymentInfo.description}
                      </Typography>
                    </Box>
                  )}

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      失敗理由
                    </Typography>
                    <Typography variant="body1" color="error.main">
                      {paymentInfo.failure_reason}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {status.error && (
            <Grid item xs={12}>
              <Alert 
                severity={status.errorType === 'rate_limit_exceeded' ? 'warning' : 'error'} 
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="body1" gutterBottom>
                    {status.error}
                  </Typography>
                  {status.retryAfter && (
                    <Typography variant="body2" color="text.secondary">
                      {new Date(status.retryAfter * 1000).toLocaleString()}以降に再試行してください。
                    </Typography>
                  )}
                </Box>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box textAlign="center">
              <RetryButton
                variant="contained"
                size="large"
                startIcon={status.isProcessing ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRetryPayment}
                disabled={
                  status.isProcessing || 
                  !stripe || 
                  (status.error && !status.shouldRetry) ||
                  (!!status.retryAfter && status.retryAfter * 1000 > Date.now())
                }
              >
                {status.isProcessing ? '処理中...' : '再決済を行う'}
              </RetryButton>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {status.error && !status.shouldRetry ? 
                  '※ このエラーは再試行できません' :
                  '※ 再決済処理には3D認証が必要な場合があります'
                }
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </StyledPaper>
      <Copyright />
    </Container>
  );
};

export default RetryPayment;